import formatAddress from '@lib/utils/formatAddress';
import { Card, Stack, Tag } from '@servicetitan/design-system';
import { CustomerBySearchType } from '../../data/types';
import TenantTag from '@components/TenantTag/TenantTag';
import styles from './CustomerSearchResultItem.module.css';
import SearchResultContactMethods from './components/SearchResultContactMethods/SearchResultContactMethods';
import CustomerSearchResulInfoItem from './components/CustomerSearchResultInfoItem';
import { inboxLocators } from '@locators/inbox';
import CustomerContext from '@components/CustomerContext/CustomerContext';
import { useMemo } from 'react';
import { CustomerSearchResultName } from './components/CustomerSearchResultName';

interface ICustomerSearchResultProps {
  customer: CustomerBySearchType;
  onClickHandler: (id: string) => void;
  selected: boolean;
  showTenantName?: boolean;
}

/**
 * Displays a single customer search result item.
 *
 * If there are more than 3 addresses, only the first 3 will be displayed and the remaining
 * will be specified with the plus sign and count.
 *
 * If there are more than one phone or email, only the first ones will be displayed
 * and the remaining will be specified with the plus sign and count.
 *
 * @param customer {Object} - customer object
 * @param onClickHandler {Function} - function to handle click event
 * @param selected {Boolean} - selected state that handles styling
 * @returns a single customer search result item composed of customer information like name, address, phone number, email and optional tenant name
 */
const CustomerSearchResultItem = ({
  customer,
  selected,
  onClickHandler,
  showTenantName,
}: ICustomerSearchResultProps) => {
  const firstThreeAddresses = customer.locations.slice(0, 3);

  const customerContextValue = useMemo(() => {
    return {
      customerName: customer.name,
      tenantId: customer.tenant.id,
      customerId: customer.id,
    };
  }, [customer]);

  return (
    <CustomerContext.Provider value={customerContextValue}>
      <Card
        id={inboxLocators.customerSearch.searchResults}
        className={styles.searchResultHover}
        active={selected}
        hoverable
        onClick={() => onClickHandler(customer.id)}
      >
        <div className='d-f column-gap-3 row-gap-4 align-items-center flex-wrap'>
          <div className='d-f gap-3 align-items-center'>
            <CustomerSearchResultName
              iconName='person'
              content={customer.name}
              className={styles.searchResultName}
            />
            {showTenantName && (
              <TenantTag tenantNameOrId={customer.tenant.id} />
            )}
            {!!customer.doNotService && (
              <div className={styles.searchResultTenant}>
                <div className='d-f'>
                  <Tag color='inactive' subtle={true}>
                    Do Not Service
                  </Tag>
                </div>
              </div>
            )}
          </div>
          <div className='d-f gap-3 align-items-center flex-wrap'>
            <Stack direction='column' spacing={1}>
              {firstThreeAddresses.map((location, index) => (
                <CustomerSearchResulInfoItem
                  key={location.id}
                  iconName='home'
                  content={formatAddress(location.address)}
                  badgeCount={
                    index === 2 ? customer.locations.length - 3 : undefined
                  }
                  className={styles.searchResultAddress}
                />
              ))}
            </Stack>
            <SearchResultContactMethods
              contacts={customer.contacts}
              compact={showTenantName}
            />
          </div>
        </div>
      </Card>
    </CustomerContext.Provider>
  );
};

export default CustomerSearchResultItem;
